/* eslint-disable prettier/prettier */
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { AvatarGroup, Box, Grid, Tab, Tabs, Typography } from '@mui/material';

// project imports
import Profile from './Profile';
import useAuth from 'hooks/useAuth';
import useConfig from 'hooks/useConfig';
import Avatar from 'ui-component/extended/Avatar';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import { useDispatch, useSelector } from 'store';

// assets
import { IconInbox } from '@tabler/icons';

import useGroups from 'hooks/useGroups';
import useLeagues from 'hooks/useLeagues';

import Overview from './Overview';
import TournamentManager from './TournamentManager';
import { getTournamentPlayerList } from 'store/slices/user';
import Leaderboard from './Leaderboard/Leaderboard';

function TabPanel({ children, value, index, ...other }) {
    return (
        <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
            {value === index && (
                <Box
                    sx={{
                        p: 0
                    }}
                >
                    {children}
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
    };
}

// ==============================|| SOCIAL PROFILE ||============================== //

const LeagueHome = () => {
    const theme = useTheme();
    const { leagueId } = useParams();
    const dispatch = useDispatch();

    const { tab } = useParams();
    const { getLeague, league } = useLeagues();

    useEffect(() => {
        getLeague(leagueId);
    }, [leagueId]);

    let selectedTab = 0;
    switch (tab) {
        case 'follower':
            selectedTab = 1;
            break;
        case 'friends':
            selectedTab = 2;
            break;
        case 'gallery':
            selectedTab = 3;
            break;
        case 'friend-request':
            selectedTab = 4;
            break;
        case 'posts':
        default:
            selectedTab = 0;
    }
    const [isLoading, setLoading] = useState(true);
    useEffect(() => {
        setLoading(false);
    }, []);

    const tabOptions = [
        {
            to: '/user/social-profile/posts',
            icon: <IconInbox stroke={1.5} size="17px" />,
            label: 'Overview'
        },
        {
            to: '/user/social-profile/posts',
            icon: <IconInbox stroke={1.5} size="17px" />,
            label: 'Leaderboard'
        }
    ];

    // Assume these are your subtabs for the Tournament Manager
    const mainTabs = [
        'Overview',
        'Divisions',
        'Check In',
        'Pools',
        'Medal Round',
        'Results',
        'Admin'
        // ... any other subtabs for the Tournament Manager
    ];

    const [activeMainTab, setActiveMainTab] = useState(0);

    // State for the active subtab of the Tournament Manager

    const handleMainTabChange = (event, newValue) => {
        setActiveMainTab(newValue);
        // If we're leaving the Tournament Manager tab, reset the subtab
    };

    // TODO : Check if user is admin of the tournament

    const eventList = league?.grouped_events;

    const [options, setOptions] = useState([]);
    useEffect(() => {
        if (eventList) {
            setOptions(
                eventList
                    .map((group) =>
                        group.events.map((event) => ({
                            group: group.group,
                            ...event
                        }))
                    )
                    .flat()
            );
        }
    }, [eventList]);

    const getContentForMainTab = (subTabIndex) => {
        switch (subTabIndex) {
            case 0:
                return <Profile league={league} />;
            case 1:
                return <Leaderboard leagueId={league?.id} />;
            case 2:
                return <TournamentManager options={options} leagueId={league?.id} groupId={league?.group_id} />;
            default:
                return <Overview eventList={league?.grouped_events} />;
        }
    };

    useEffect(() => {
        dispatch(getTournamentPlayerList(leagueId, '', 10, 1));
    }, []);
    const { tournamentRoster, totalParticipants } = useSelector((state) => state.user);

    return (
        <Grid container spacing={gridSpacing} data-test-id="views/leagues/LeagueHome/index">
            <Grid container justifyContent="center" xs={12} mt={2} />
            <Grid item xs={12}>
                <MainCard
                    contentSX={{
                        p: 1.5,
                        paddingBottom: '0px !important',
                        [theme.breakpoints.down('lg')]: {
                            textAlign: 'center'
                        }
                    }}
                >
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12} md={12}>
                            <Grid container spacing={gridSpacing}>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="h2">{league?.name}</Typography>
                                    {league?.club && <Typography variant="subtitle2">{league?.club?.name}</Typography>}
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Grid
                                        container
                                        spacing={1}
                                        sx={{
                                            justifyContent: 'flex-end',
                                            [theme.breakpoints.down('lg')]: {
                                                justifyContent: 'center'
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container justifyContent="space-between" alignItems="center" ml={2}>
                                <Grid container flexDirection="column" justifyContent="flex-start" xs={12} md={4}>
                                    <Grid item alignSelf="flex-start">
                                        <AvatarGroup
                                            max={4}
                                            total={totalParticipants}
                                            renderSurplus={(surplus) => <Typography style={{ color: 'white' }}>+{surplus}</Typography>}
                                        >
                                            {tournamentRoster.map((usr) => (
                                                <Avatar alt="User 1" src={usr.user.user_attributes.profile_picture_url} />
                                            ))}
                                        </AvatarGroup>
                                    </Grid>
                                </Grid>

                                <Grid container xs={12} md={8} justifyContent="flex-end" alignSelf="flex-end">
                                    <Tabs
                                        value={activeMainTab}
                                        onChange={handleMainTabChange}
                                        variant="scrollable"
                                        sx={{
                                            marginTop: 2.5,
                                            '& .MuiTabs-flexContainer': {
                                                border: 'none'
                                            },
                                            '& a': {
                                                minHeight: 'auto',
                                                minWidth: 10,
                                                py: 1.5,
                                                px: 1,
                                                mr: 2.25,
                                                color: 'grey.700',
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            },
                                            '& a.Mui-selected': {
                                                color: 'primary.main'
                                            },
                                            '& a > svg': {
                                                marginBottom: '4px !important',
                                                mr: 1.25
                                            }
                                        }}
                                    >
                                        {tabOptions.map((option, index) => (
                                            <Tab
                                                key={index}
                                                component={Link}
                                                // to={option.to}
                                                icon={option.icon}
                                                label={option.label}
                                                {...a11yProps(index)}
                                            />
                                        ))}
                                    </Tabs>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </MainCard>
            </Grid>
            <Grid item xs={12}>
                {mainTabs.map((tab, index) => (
                    <TabPanel value={activeMainTab} index={index} key={index}>
                        {/* This is where you determine what to show for each main tab */}
                        {getContentForMainTab(index)}
                    </TabPanel>
                ))}
            </Grid>
        </Grid>
    );
};

export default LeagueHome;
